import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { AppStatusSectionConfigs } from "./AppStatusSectionConfigs";
import * as yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import { Badge, Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import DynamicSections from "./dynamicsections";
import SmartField from "../../../../rhf/smartfield";
import useMessageStore from "../../../../stores/message-store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import useUserStore from "../../../../stores/user-store";
import { useOutletContext } from "react-router-dom";
import {
    useApplicationStatusAdd,
    useApplicationStatusDefinitionsQuery,
    useApplicationStatusHistoryQuery,
    useApplicationStatusRecordQuery,
    useApplicationStatusTransitionSummaryQuery,
    useApplicationStatusUpdate,
    useApplicationStatusVoid
} from "../../../../new/query";

const EMPTY_ALLOWED_STATUSES = [];
const EMPTY_TABLE_DATA = [];
const NOTE_MAX_LENGTH = 500;

const getReasonPayloadValue = (value) => {
    if(value == null || value.trim() === '') return null;

    return value;
};

const addErrorMessage = (messages,value) => {
    if(!value) return;

    if(Array.isArray(value)){
        value.forEach(item => addErrorMessage(messages,item));
        return;
    }

    if(typeof value === 'string'){
        value
            .split('\n')
            .map(message => message.trim())
            .filter(Boolean)
            .forEach(message => messages.push(message));
        return;
    }

    if(value.message){
        addErrorMessage(messages,value.message);
    }
};

const getMutationErrorMessages = (error,fallbackMessage) => {
    const messages = [];
    const data = error?.data;

    addErrorMessage(messages,data?.domainErrors);
    addErrorMessage(messages,data?.errors);
    addErrorMessage(messages,data?.message);

    if(messages.length === 0){
        addErrorMessage(messages,error?.message);
    }

    if(messages.length === 0){
        addErrorMessage(messages,fallbackMessage);
    }

    return [...new Set(messages)];
};

const buildValidationSchema = (selectedStatus,formValues = {},lookupData = {},statusOptions = [],editId = null) => {
    let schema = {
        statusid: yup.string().required('Please make a selection'),
        note: yup.string().when('statusid', {
            is: (val) => {
                if(val === '') return false;

                const foundStatus = statusOptions.find(item => `${item.id}` === `${val}`);

                return foundStatus?.requiresReasonForChange === true;
            },
            then: (schema) => schema.required('Required').typeError('Invalid'),
            otherwise: (schema) => schema.notRequired().nullable()
        }).max(NOTE_MAX_LENGTH,`Reason can not exceed ${NOTE_MAX_LENGTH} characters.`)
    }

    if(editId){
        const enhancedSchema = {
            begindate: yup.string()
                .required("*Required"),
            enddate: yup.string()
                .transform((value,originalValue) => {
                    return originalValue === '' ? undefined : value
                })
                .test('is-after-begin','*End Date must be after begin date.',function(value){
                    const {begindate} = this.parent; //Get Begin Date
                    if(!value || !begindate) return true;

                    return new Date(value) > new Date(begindate);
                })
                .nullable()
        };

        schema = {...schema,...enhancedSchema};
    }
    else{
        const dateSchema = {
            begindate: yup.date()
            .transform((value,originalValue) => {
                return originalValue === '' ? undefined : value
            })
            .when('statusid', {
                is: (val) => {
                    if(val === '') return false; //Not required when not selected.

                    const foundStatus = statusOptions.find(item => item.id === val);
                    
                    return foundStatus.allowDateEntry || foundStatus.isInfoOnly
                },
                then: (schema) => schema.required('*Required'),
                otherwise: (schema) => schema.nullable().notRequired()
            })
        }

        schema = {...schema,...dateSchema};
    }

    if(selectedStatus){

        AppStatusSectionConfigs.forEach(({key,getValidationSchema}) => {
            if(selectedStatus[key] && getValidationSchema){
                const sectionSchema = getValidationSchema(formValues,lookupData);
                schema = {...schema, ...sectionSchema};
            }
        })
    }

    return yup.object().shape(schema);
}

const ChangeAppStatusNew = ({}) => {
    const {ApplicationID} = useOutletContext();

    const [selectedStatus,setSelectedStatus] = useState(null);
    const [showVoids,setShowVoids] = useState(false);
    const [showInformationOnly,setShowInformationOnly] = useState(true);
    const [editId,setEditId] = useState(null);
    const [mutationErrors,setMutationErrors] = useState([]);
    const previousStatusIdRef = useRef(null);

    const addToast = useMessageStore(state => state.addToast);

    const permissions = useUserStore((state) => state.permissions);

    const _permissionCheck = (permissionString) => permissions.includes(permissionString);

    const hasAddPermission = _permissionCheck("Application.History.Add");
    const hasEditPermission = _permissionCheck("Application.History.Update");

    const defsQ = useApplicationStatusDefinitionsQuery();
    const historyQ = useApplicationStatusHistoryQuery(ApplicationID,showVoids,showInformationOnly);
    const transitionSummaryQ = useApplicationStatusTransitionSummaryQuery(ApplicationID);
    const recordQ = useApplicationStatusRecordQuery(ApplicationID,editId,!!editId);

    const lookupData = useMemo(() => {
        const {allowedstatuses,...lookupValues} = defsQ.data ?? {};

        return lookupValues;
    },[defsQ.data]);

    const allowedStatuses = defsQ.data?.allowedstatuses ?? EMPTY_ALLOWED_STATUSES;
    const tableData = historyQ.data ?? EMPTY_TABLE_DATA;
    const transitionSummary = transitionSummaryQ.data;
    const isLoading = defsQ.isLoading || historyQ.isLoading;
    const isError = defsQ.isError || historyQ.isError;

    const initialValues = useMemo(() => ({
        statusid: '',
        note: '',
        begindate: '',
        enddate: '',
        contactname: '',
        contactemail: '',
        contactphone: '',
        propertyaddress: '',
        propertycity: '',
        propertystate: '',
        propertyzip: '',
        housingnotes: '',
        reason: '',
        facility: '',
        deathcircumstance: '',
        deathcircumstanceother: '',
        abuseneglectexploitation: false,
        breakdownbackupsystem: false,
        disenrolledreason: '',
        disenrolledreasonother: '',
        disenroll365: '',
        waiversaftermfp: [],
        holdtilldate: ''
    }),[]);

    const validationSchema = useMemo(() => buildValidationSchema(selectedStatus,{},lookupData,allowedStatuses,editId),[selectedStatus,lookupData,allowedStatuses,editId]);

    const {register,handleSubmit,watch,resetField,reset,trigger,control,formState: {errors,isDirty,isSubmitting}} = useForm({defaultValues: initialValues,resolver: yupResolver(validationSchema)});

    const hideMutationErrors = () => {
        setMutationErrors([]);
    }

    const showMutationErrors = (error,fallbackMessage) => {
        const messages = getMutationErrorMessages(error,fallbackMessage);
        setMutationErrors(messages);

        return messages;
    }

    const addSuccessCallback = () => {
        hideMutationErrors();

        addToast(
            {
                id: Math.random(),
                body: "The Status was added succesfully!",
                autoHide: true,
                errorFlag: false,
                timestamp: Date.now() - 1000
            }
        )

        reset();
    }

    const addErrorCallback = (error) => {
        showMutationErrors(error,"An error occured when adding the status.");
    }

    const updateSuccessCallback = () => {
        hideMutationErrors();

        addToast(
            {
                id: Math.random(),
                body: "The Status was updated succesfully!",
                autoHide: true,
                errorFlag: false,
                timestamp: Date.now() - 1000
            }
        )

        reset(initialValues);
        setEditId(null);
    }

    const updateErrorCallback = (error) => {
        showMutationErrors(error,"An error occured when updating the status.");
    }

    const voidErrorCallback = (error) => {
        showMutationErrors(error,"An error occured when voiding the record.");
    }

    const addStatusMutation = useApplicationStatusAdd(ApplicationID,addSuccessCallback,addErrorCallback);
    const updateStatusMutation = useApplicationStatusUpdate(ApplicationID,updateSuccessCallback,updateErrorCallback);
    const voidStatusMutation = useApplicationStatusVoid(ApplicationID,showVoids,showInformationOnly,null,voidErrorCallback);
    const formIsSubmitting = isSubmitting || addStatusMutation.isPending || updateStatusMutation.isPending || recordQ.isFetching;
    const statusIsMutating = formIsSubmitting || voidStatusMutation.isPending;

    const watchedValues = watch();
    const watchedStatusId = watchedValues.statusid;
    const noteCharactersRemaining = NOTE_MAX_LENGTH - (watchedValues.note ?? '').length;

    useEffect(() => {
        if(previousStatusIdRef.current !== watchedStatusId){
            setMutationErrors([]);
            previousStatusIdRef.current = watchedStatusId;
        }

        const found = allowedStatuses.find(x => x.id === watchedStatusId) || null;

        setSelectedStatus(found);

        if(found){
            if(!found.allowDateEntry && !found.isInfoOnly){
                resetField('begindate');
            }

            AppStatusSectionConfigs.forEach(({key,fieldNames}) => {
                if(!found[key]){
                    fieldNames.forEach(field => resetField(field));
                }
            })
        }
        else{
            //Reset all sub sections on selection of default status
            AppStatusSectionConfigs.forEach(({fieldNames}) => {
                fieldNames.forEach(field => resetField(field));
            });

            resetField('begindate');
            resetField('note');
            trigger('note');
        }
    },[watchedStatusId,allowedStatuses,resetField,trigger])

    useEffect(() => {
        if(!editId || !recordQ.data) return;
        if(`${recordQ.data.id}` !== `${editId}`) return;

        const {id,...Values} = recordQ.data;

        reset(Values); //Reset form initial values to what was returned by the server
        setEditId(id);
    },[editId,recordQ.data,reset])

    useEffect(() => {
        if(!editId || !recordQ.isError) return;

        setEditId(null);
        reset(initialValues);

        addToast(
            {
                id: Math.random(),
                body: "An error occured when retrieving the status.",
                autoHide: true,
                errorFlag: true,
                timestamp: Date.now() - 1000
            }
        )
    },[editId,recordQ.isError,reset,initialValues,addToast])

    const _onVoidChange = (e) => {
        hideMutationErrors();
        setShowVoids(e.target.checked);
    }

    const _onInfoOnlyChange = (e) => {
        hideMutationErrors();
        setShowInformationOnly(e.target.checked);
    }

    const _toggleVoid = (e,id) => {
        hideMutationErrors();
        voidStatusMutation.mutate({
            ApplicationID: ApplicationID,
            StatusID: id,
            IsVoid: e.target.checked
        });
    }

    const onSubmit = async(data) => {
        hideMutationErrors();

        const payload = {
            ApplicationID: ApplicationID,
            statusid: data.statusid,
            note: getReasonPayloadValue(data.note)
        };

        AppStatusSectionConfigs.forEach(({key,fieldNames,payloadName}) => {
            if(selectedStatus?.[key]){
                const sectionData = {};

                fieldNames.forEach(field => {
                    if(data[field] !== undefined && data[field] !== ''){
                        sectionData[field] = data[field];
                    }
                });

                if(Object.keys(sectionData).length > 0){
                    payload[payloadName] = sectionData;
                }
            }
        })

        if(payload.holdInfo){
            payload.holdInfo.holdtilldate = payload.holdInfo.holdtilldate.toISOString().split('T')[0];
        }

        if(!editId){
            payload.begindate = data.begindate?.toISOString().split('T')[0];
            payload.enddate = null;

            try{
                await addStatusMutation.mutateAsync(payload);
            }
            catch{
                // Error toast is handled by the mutation callback.
            }
        }
        else{
            payload.begindate = data.begindate;
            payload.enddate = data.enddate;

            try{
                await updateStatusMutation.mutateAsync({...payload,HistoryID: editId});
            }
            catch{
                // Error toast is handled by the mutation callback.
            }
        }
    }

    if(isLoading){
        return(
            <div>loading...</div>
        )
    }

    if(isError){
        return(
            <div>error</div>
        )
    }

    //Handles switching to edit mode
    const _editSelect = (statusid) => {
        hideMutationErrors();

        //Smooth scroll to top editor panel
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        })

        reset(initialValues);
        setEditId(statusid);
    }

    const selectedStatusVal = allowedStatuses.find(x => x.id === watchedStatusId) || null;
    const mutationErrorList = mutationErrors.length > 0
        ? (
            <ul className="text-danger mt-2 mb-0 ps-3">
                {mutationErrors.map(message => <li key={message}>{message}</li>)}
            </ul>
        )
        : null;

    return(
        <>
            <div className="clientCard">
                <div className="clientCardInner">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <h4 className="text-center">Kentucky Transitions Change Application Status</h4>
                        <Card>
                            <Card.Body>
                                <Row>
                                    <Col md={12} className="d-block d-md-none">
                                        <TransitionCard summary={transitionSummary} isLoading={transitionSummaryQ.isLoading} />
                                    </Col>
                                    <Col md={8}>
                                        <Row>
                                            <Col xs={12}>
                                                <SmartField
                                                    label="Status"
                                                    name="statusid"
                                                    register={register}
                                                    errors={errors}
                                                    type="select"
                                                    options={allowedStatuses}
                                                    style={{width: '100%'}}
                                                    disabled={editId !== null}
                                                />
                                            </Col>
                                            <Col xs={12}>
                                                <SmartField
                                                    label="Note/Reason For Change"
                                                    name="note"
                                                    register={register}
                                                    errors={errors}
                                                    type="textarea"
                                                    style={{width: '100%'}}
                                                    maxLength={NOTE_MAX_LENGTH}
                                                />
                                                <small className={noteCharactersRemaining <= 50 ? "text-warning d-block mt-1" : "text-muted d-block mt-1"}>
                                                    {noteCharactersRemaining} characters remaining
                                                </small>
                                            </Col>
                                            {
                                                editId !== null
                                                &&
                                                <Col xs={12}>
                                                    <small style={{color: 'orange'}}>*All Dates/Times entered are assumed to be in Eastern Daylight Time/Eastern Standard Time</small>
                                                </Col>
                                            }
                                            {
                                                (selectedStatusVal?.allowDateEntry || selectedStatusVal?.isInfoOnly || editId !== null)
                                                &&
                                                <Col xs={12} md={4}>
                                                    <SmartField
                                                        label="Begin Date of Status"
                                                        name="begindate"
                                                        register={register}
                                                        errors={errors}
                                                        type={`${editId !== null ? 'datetime-local' : 'date'}`}
                                                        style={{width: '100%'}}
                                                    />
                                                </Col>
                                            }
                                            {
                                                (editId !== null)
                                                &&
                                                <Col xs={12} md={4}>
                                                    <SmartField
                                                        label="End Date of Status"
                                                        name="enddate"
                                                        register={register}
                                                        errors={errors}
                                                        type="datetime-local"
                                                        style={{width: '100%'}}
                                                    />
                                                </Col>
                                            }
                                        </Row>
                                        <div >
                                            <Row>
                                                <DynamicSections
                                                    selectedStatus={selectedStatus}
                                                    register={register}
                                                    errors={errors}
                                                    control={control}
                                                    resetFields={resetField}
                                                    lookupdata={lookupData}
                                                />
                                            </Row>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <TransitionCard displayCss='d-none d-md-block' summary={transitionSummary} isLoading={transitionSummaryQ.isLoading} />
                                        {
                                            editId
                                            ?
                                                <>
                                                <div className="d-flex justify-content-between align-items-center mt-3">
                                                    <Button type="button" variant="danger" className="" onClick={() => {hideMutationErrors(); setEditId(null); reset(initialValues);}} disabled={formIsSubmitting}>
                                                        Cancel
                                                    </Button>
                                                    <Button type="submit" variant="primary" className="" disabled={!isDirty || formIsSubmitting || !hasEditPermission}>
                                                        {
                                                            formIsSubmitting
                                                            ?
                                                                <Spinner animation='border' size='sm' />
                                                            :
                                                                <>Save Changes</>
                                                        }
                                                    </Button>
                                                </div>
                                                {mutationErrorList}
                                                </>
                                            :
                                                <>
                                                <Button type="submit" variant="primary" className="mt-3 w-100" disabled={!isDirty || formIsSubmitting || !hasAddPermission}>
                                                    {
                                                        formIsSubmitting
                                                        ?
                                                            <Spinner animation='border' size='sm' />
                                                        :
                                                            <>Submit New Status</>
                                                    }
                                                </Button>
                                                    {mutationErrorList}
                                                    {!hasAddPermission && <small style={{color: 'red'}}>*You currently do not have permission to add a status.</small>}
                                                </>
                                        }
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </form>
                </div>
            </div>
            <div className="clientCard mt-4">
                <div className="clientCardInner">
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th colSpan={hasEditPermission ? 9 : 8}>
                                    <div className='d-flex flex-column justify-content-center align-items-start flex-md-row justify-content-md-between align-items-md-center '>
                                        <Col xs={12} md={2} className="order-2 order-md-1">
                                            <div className="form-check">
                                                <label className="form-check-label" htmlFor="show-void-cbx">
                                                    <input className="form-check-input" type="checkbox" id="show-void-cbx" onChange={_onVoidChange} checked={showVoids} disabled={statusIsMutating} />
                                                    Show Voided Statuses
                                                </label>
                                            </div>
                                        </Col>
                                        <Col xs={12} md={8} className="order-1 order-md-2">
                                            <h4 className="text-md-center">Application Status History</h4>
                                        </Col>
                                        <Col xs={12} md={2} className="order-3">
                                            <div className="form-check">
                                                <label className="form-check-label" htmlFor="show-info-only-cbx">
                                                    <input className="form-check-input" type="checkbox" id="show-info-only-cbx" onChange={_onInfoOnlyChange} checked={showInformationOnly} disabled={statusIsMutating} />
                                                    Show Info Only Statuses
                                                </label>
                                            </div>
                                        </Col>
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                <th className="text-center">Void</th>
                                <th>Status</th>
                                <th>Reason</th>
                                <th>Begin</th>
                                <th>End</th>
                                <th>Last Modified</th>
                                <th>Last Modified By</th>
                                <th>Submitted By</th>
                                {hasEditPermission &&<th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {
                                tableData.map((item) => (
                                    <tr key={item.id}>
                                        <td className="text-center">
                                            <input type="checkbox"  aria-label="Void Status" title="Void Status" checked={item.isvoid} disabled={!hasEditPermission || statusIsMutating} onChange={(e) => _toggleVoid(e,item.id)}/>
                                        </td>
                                        <td style={{whiteSpace: 'pre-line'}}>{`${item.status}\n\n${item.additionalstatus}`}</td>
                                        <td>{item.reason}</td>
                                        <td>{item.begin}</td>
                                        <td>{item.end}</td>
                                        <td>{item.lastmodifieddate}</td>
                                        <td>{item.lastmodifiedby}</td>
                                        <td>{item.createdby}</td>
                                        {
                                            (hasEditPermission)
                                            &&
                                            <td>
                                                {
                                                    allowedStatuses.find(val => val.id === item.statusid)
                                                    ?
                                                        editId === item.id
                                                        ?
                                                            <Badge pill bg="info">Editing</Badge>
                                                        :
                                                            <Button size="sm" type="button" variant="secondary" title="Edit Status" aria-label="Edit Status" onClick={() => _editSelect(item.id)} disabled={statusIsMutating}>
                                                                <FontAwesomeIcon icon={faPenToSquare} />
                                                            </Button>
                                                    :
                                                        null
                                                }
                                            </td>
                                        }
                                    </tr>
                                ))
                            }
                        </tbody>
                    </Table>
                </div>
            </div>
        </>
    )
}

const TransitionCard = ({ displayCss = '', summary, isLoading = false }) => {
    const totalDays = summary?.totaldaysintransition ?? 0;
    const date365 = summary?.date365 ?? 'N/A';

    return (
        <Card className={`border-0 bg-light ${displayCss}`}>
            <Card.Body className="d-flex flex-column justify-content-center">
                <p>Total Days in MFP Transition: <span className="fw-bold">{isLoading ? <Spinner animation="border" size="sm" /> : totalDays}</span></p>
                <p>Based on current data client will reach their 365th day in MFP on: <span className="fw-bold">{isLoading ? <Spinner animation="border" size="sm" /> : date365}</span></p>
            </Card.Body>
        </Card>
    );
};

export default ChangeAppStatusNew;
